import { Component, effect, inject, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { from, forkJoin } from 'rxjs';
import { concatMap, tap } from 'rxjs/operators';
import { MonthPickerComponent } from '../../components/month-picker/month-picker';
import { SearchableDropdown } from '../../components/searchable-dropdown/searchable-dropdown';
import { ReportV2Service, ClientV2 } from '../../services/report-v2.service';
import { SnackbarService } from '../../services/snackbar.service';
import { FileDocumentType } from '../../models/provisioning';
import { Loader } from '../../loader/loader';
import { Router } from '@angular/router';
import { ValidationMessagesTexts } from '../../constants/validation-messages.constants';
import { ApiResponseMessages } from '../../constants/api-response-messages.constants';
import { Labels } from '../../constants/labels-constant';
import { DialogueTexts } from '../../constants/dialog-texts.constant';
import { DialogueBox } from '../../dialogue-box/dialogue-box';
import { ProvisioningService } from '../../services/provisioning.service';
import { NumbersOnlyDirective } from '../../directives/numbers-only';

export const DURATION_OPTIONS = [
  { label: 'Quarterly', value: 'quarterly' },
  { label: 'Semi-Annually', value: 'semi-annually' },
  { label: 'Annually', value: 'annually' },
];

interface AdditionalSection {
  label: string;
  file: File | null;
  validationError: string;
  sequence: number | null;
}

interface UploadFileEntry {
  file: File;
  documentType: FileDocumentType;
  title: string | null;
  sequence: number | null;
}

@Component({
  selector: 'app-update-from-data',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MonthPickerComponent,
    SearchableDropdown,
    Loader,
    DialogueBox,
    NumbersOnlyDirective
  ],
  templateUrl: './update-from-data.html',
  styleUrls: ['./update-from-data.scss'],
})
export class UpdateFromDataComponent implements OnInit {
  private static readonly MAX_FILE_BYTES = 100 * 1024 * 1024;
  private static readonly MAX_RUN_BYTES = 500 * 1024 * 1024;
  private static readonly MAX_TOTAL_FILES = 10;
  private static readonly MAX_ADDITIONAL_SECTIONS = 8;

  private static readonly PRIOR_REPORT_EXTENSIONS = new Set(['docx', 'dotx']);
  private static readonly DATA_FILE_EXTENSIONS = new Set(['xlsx', 'xls', 'csv']);
  private static readonly ADDITIONAL_EXTENSIONS = new Set(['pdf']);
  private static readonly MESSAGES = ValidationMessagesTexts.UPDATE_FROM_DATA;
  private static readonly API_MESSAGES = ApiResponseMessages.UPDATE_FROM_DATA;

  private reportV2Service = inject(ReportV2Service);
  private snackbar = inject(SnackbarService);
  private router = inject(Router); 
  private provisioningService = inject(ProvisioningService);

  @Input() projectId?: number;
  /** Client identifier forwarded to the file-provisioning panel. */
  @Input() client: string | number | null = null;
  /** Site identifier forwarded to the file-provisioning panel. */
  @Input() site: string | number | null = null;

  readonly durationOptions = DURATION_OPTIONS;
  readonly reportTypes = this.reportV2Service.getReportTypes();
  readonly labels = Labels.UPDATE_FROM_DATA;
  readonly commonStepLabels = Labels.COMMON.STEPS;
  readonly dialogTexts = DialogueTexts;

  private chevronClientId: number | null = null;
  isSubmitting = false;
  isLoading = false;
  uploadedCount = 0;
  uploadProgress = '';
  isReportStatusDialogOpen = false;
  reportStatusDialogMessage = '';
  reportStatusDialogButtonText = '';
  reportId = 0;

  /** Tracks the active step in the Initiate → Generate → Review → Approved stepper. */
  currentStep = 1;



  reportFile: File | null = null;
  dataFiles: File[] = [];
  isReportDragOver = false;
  isDataDragOver = false;

  additionalSections: AdditionalSection[] = [{ label: '', file: null, validationError: '', sequence: 1 }];
  reportUploadValidationError = '';
  dataUploadValidationError = '';

  form!: FormGroup;

  constructor(private fb: FormBuilder) {
    // Watch for changes to clients and load report types when Chevron is available
    effect(() => {
      const clients = this.reportV2Service.getClients()();
      if (clients.length > 0) {
        this.loadChevronReportTypes();
      }
    });

    // Default Report Type to the first option once types are available.
    effect(() => {
      const types = this.reportTypes();
      if (!this.form || !types.length) {
        return;
      }

      const control = this.form.get('reportType');
      if (!control) {
        return;
      }

      const currentValue = control.value;
      const hasValidSelection = currentValue !== null && currentValue !== undefined && currentValue !== ''
        && types.some((type) => type.id === currentValue);

      if (!hasValidSelection) {
        control.setValue(types[0].id);
      }
    });
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      reportName: [null, Validators.required],
      projectId: [null, [Validators.required, Validators.pattern(/^\d{8}$/)]],
      reportType: [null, Validators.required],
      duration: [null, Validators.required],
      startMonthYear: [null, Validators.required],
    });

    // Load clients - this will trigger the effect to find Chevron and load report types
    this.reportV2Service.loadClients();
  }

  private loadChevronReportTypes(): void {
    const clients = this.reportV2Service.getClients()();
    const chevronClient = clients.find(
      (client: ClientV2) => client.clientName === 'Chevron' && client.isActive
    );
    if (chevronClient) {
      this.chevronClientId = chevronClient.clientId;
      this.reportV2Service.loadReportTypes(chevronClient.clientId);
    }
  }

  get isAnalyzeDisabled(): boolean {
    return (
      this.form.invalid ||
      !this.reportFile ||
      !this.dataFiles.length ||
      this.hasUploadValidationError ||
      this.hasAdditionalValidationError ||
      this.isSubmitting
    );
  }

  private get hasUploadValidationError(): boolean {
    return !!this.reportUploadValidationError || !!this.dataUploadValidationError;
  }

  private get hasAdditionalValidationError(): boolean {
    return this.additionalSections.some((section) => !!section.validationError);
  }

  isSectionLabelValidationError(error: string): boolean {
    return error === UpdateFromDataComponent.MESSAGES.SECTION_LABEL_REQUIRED;
  }

  get dataFileNames(): string {
    return this.dataFiles.length
      ? this.dataFiles.map((f) => f.name).join(', ')
      : this.labels.NO_FILE_SELECTED;
  }

  async onReportSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files.length ? input.files[0] : null;
    await this.setReportFile(file, input);
  }

  async onDataSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    await this.setDataFiles(files, input);
  }

  onDropZoneDragOver(event: DragEvent, zone: 'report' | 'data'): void {
    event.preventDefault();
    event.stopPropagation();
    if (zone === 'report') {
      this.isReportDragOver = true;
      return;
    }
    this.isDataDragOver = true;
  }

  onDropZoneDragLeave(event: DragEvent, zone: 'report' | 'data'): void {
    event.preventDefault();
    event.stopPropagation();
    if (zone === 'report') {
      this.isReportDragOver = false;
      return;
    }
    this.isDataDragOver = false;
  }

  async onReportDrop(event: DragEvent): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    this.isReportDragOver = false;

    const files = event.dataTransfer?.files ? Array.from(event.dataTransfer.files) : [];
    await this.setReportFile(files.length ? files[0] : null);
  }

  async onDataDrop(event: DragEvent): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    this.isDataDragOver = false;

    const files = event.dataTransfer?.files ? Array.from(event.dataTransfer.files) : [];
    await this.setDataFiles(files);
  }

  private async setReportFile(file: File | null, input?: HTMLInputElement): Promise<void> {
    if (!file) {
      this.reportFile = null;
      this.reportUploadValidationError = '';
      return;
    }
     // Clear any previous selection; set again once the new file passes validation.
     this.reportFile = null;

    const ext = this.getExtension(file.name);
    if (!UpdateFromDataComponent.PRIOR_REPORT_EXTENSIONS.has(ext)) {
      this.reportUploadValidationError = UpdateFromDataComponent.MESSAGES.PRIOR_REPORT_EXTENSION_INVALID;
      if (input) {
        input.value = '';
      }
      return;
    }

    const fileHealthError = await this.validateFileHealth(file, ext);
    if (fileHealthError) {
      this.reportUploadValidationError = fileHealthError;
      if (input) {
        input.value = '';
      }
      return;
    }

    const projectedCount = (file ? 1 : 0) + this.dataFiles.length + this.getAdditionalFilesCount();
    if (projectedCount > UpdateFromDataComponent.MAX_TOTAL_FILES) {
      this.reportUploadValidationError = this.formatMessage(
        UpdateFromDataComponent.MESSAGES.MAX_FILES_ALLOWED,
        { maxFiles: UpdateFromDataComponent.MAX_TOTAL_FILES }
      );
      if (input) {
        input.value = '';
      }
      return;
    }

    const projectedBytes = file.size + this.getDataFilesBytes() + this.getAdditionalFilesBytes();
    if (projectedBytes > UpdateFromDataComponent.MAX_RUN_BYTES) {
      this.reportUploadValidationError = UpdateFromDataComponent.MESSAGES.TOTAL_SIZE_LIMIT_EXCEEDED;
      if (input) {
        input.value = '';
      }
      return;
    }

    this.reportFile = file;
    this.reportUploadValidationError = '';
  }

  private async setDataFiles(files: File[], input?: HTMLInputElement): Promise<void> {
    if (!files.length) {
      this.dataFiles = [];
      this.dataUploadValidationError = '';
      return;
    }
    this.dataFiles = [];

    if (files.length > 1) {
      this.dataUploadValidationError = UpdateFromDataComponent.MESSAGES.SINGLE_DATA_FILE_ONLY;
      if (input) {
        input.value = '';
      }
      return;
    }

    for (const file of files) {
      const ext = this.getExtension(file.name);
      if (!UpdateFromDataComponent.DATA_FILE_EXTENSIONS.has(ext)) {
        this.dataUploadValidationError = UpdateFromDataComponent.MESSAGES.DATA_FILES_EXTENSION_INVALID;
        if (input) {
          input.value = '';
        }
        return;
      }
      const fileHealthError = await this.validateFileHealth(file, ext);
      if (fileHealthError) {
        this.dataUploadValidationError = fileHealthError;
        if (input) {
          input.value = '';
        }
        return;
      }
    }

    const duplicates = this.findDuplicateNames(files);
    if (duplicates.length) {
      this.dataUploadValidationError = this.formatMessage(
        UpdateFromDataComponent.MESSAGES.DUPLICATE_FILE_NAMES,
        { duplicates: duplicates.join(', ') }
      );
      if (input) {
        input.value = '';
      }
      return;
    }

    const projectedCount = (this.reportFile ? 1 : 0) + files.length + this.getAdditionalFilesCount();
    if (projectedCount > UpdateFromDataComponent.MAX_TOTAL_FILES) {
      this.dataUploadValidationError = this.formatMessage(
        UpdateFromDataComponent.MESSAGES.MAX_FILES_ALLOWED,
        { maxFiles: UpdateFromDataComponent.MAX_TOTAL_FILES }
      );
      if (input) {
        input.value = '';
      }
      return;
    }

    const projectedBytes = (this.reportFile?.size ?? 0) + this.sumFileSizes(files) + this.getAdditionalFilesBytes();
    if (projectedBytes > UpdateFromDataComponent.MAX_RUN_BYTES) {
      this.dataUploadValidationError = UpdateFromDataComponent.MESSAGES.TOTAL_SIZE_LIMIT_EXCEEDED;
      if (input) {
        input.value = '';
      }
      return;
    }

    this.dataFiles = [files[0]];
    this.dataUploadValidationError = '';
  }

  addAdditionalRow(): void {
    if (this.additionalSections.length < UpdateFromDataComponent.MAX_ADDITIONAL_SECTIONS) {
      this.additionalSections.push({
        label: '',
        file: null,
        validationError: '',
        sequence: this.additionalSections.length + 1,
      });
    }
  }

  removeAdditionalRow(index: number): void {
    if (this.additionalSections.length === 1) {
      return;
    }
    this.additionalSections.splice(index, 1);
    this.updateAdditionalSectionSequences();
  }

  onSectionLabelChange(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    this.additionalSections[index].label = input.value;

    const hasFile = !!this.additionalSections[index].file;
    const hasLabel = input.value.trim().length > 0;
    if (hasFile && !hasLabel) {
      this.additionalSections[index].validationError = UpdateFromDataComponent.MESSAGES.SECTION_LABEL_REQUIRED;
      return;
    }

    if (hasLabel && !hasFile) {
      this.additionalSections[index].validationError = UpdateFromDataComponent.MESSAGES.SECTION_FILE_REQUIRED;
      return;
    }

    this.additionalSections[index].validationError = '';
  }

  async onAdditionalFileSelected(event: Event, index: number): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files.length ? input.files[0] : null;
    this.additionalSections[index].validationError = '';
    if (!file) {
      this.additionalSections[index].file = null;
      const sectionLabel = this.additionalSections[index].label.trim();
      if (sectionLabel) {
        this.additionalSections[index].validationError = UpdateFromDataComponent.MESSAGES.SECTION_FILE_REQUIRED;
      }
      return;
    }

    const ext = this.getExtension(file.name);
    if (!UpdateFromDataComponent.ADDITIONAL_EXTENSIONS.has(ext)) {
      this.additionalSections[index].validationError = UpdateFromDataComponent.MESSAGES.ADDITIONAL_FILE_EXTENSION_INVALID;
      input.value = '';
      return;
    }

    const fileHealthError = await this.validateFileHealth(file, ext);
    if (fileHealthError) {
      this.additionalSections[index].validationError = fileHealthError;
      input.value = '';
      return;
    }

    const additionalCountExcludingCurrent = this.getAdditionalFilesCount(index);
    const projectedCount = (this.reportFile ? 1 : 0) + this.dataFiles.length + additionalCountExcludingCurrent + 1;
    if (projectedCount > UpdateFromDataComponent.MAX_TOTAL_FILES) {
      this.additionalSections[index].validationError = this.formatMessage(
        UpdateFromDataComponent.MESSAGES.MAX_FILES_ALLOWED,
        { maxFiles: UpdateFromDataComponent.MAX_TOTAL_FILES }
      );
      input.value = '';
      return;
    }

    const projectedBytes = (this.reportFile?.size ?? 0) + this.getDataFilesBytes() + this.getAdditionalFilesBytes(index) + file.size;
    if (projectedBytes > UpdateFromDataComponent.MAX_RUN_BYTES) {
      this.additionalSections[index].validationError = UpdateFromDataComponent.MESSAGES.TOTAL_SIZE_LIMIT_EXCEEDED;
      input.value = '';
      return;
    }

    const existingNames = this.getAllSelectedFileNames(index);
    if (existingNames.has(file.name.toLowerCase())) {
      this.additionalSections[index].validationError = this.formatMessage(
        UpdateFromDataComponent.MESSAGES.FILE_ALREADY_SELECTED,
        { fileName: file.name }
      );
      input.value = '';
      return;
    }

    this.additionalSections[index].file = file;

    const sectionLabel = this.additionalSections[index].label.trim();
    if (!sectionLabel) {
      this.additionalSections[index].validationError = UpdateFromDataComponent.MESSAGES.SECTION_LABEL_REQUIRED;
      return;
    }

    this.additionalSections[index].validationError = '';
  }

  onCancel(): void {
    // TODO: wire up navigation/cancel logic
  }

  closeReportStatusDialog(): void {
    this.isReportStatusDialogOpen = false;
    void this.router.navigate(['/reports']).catch(() => {
      this.snackbar.showCustom({
        type: 'error',
        title: ValidationMessagesTexts.TOASTER.ERROR.TITLE,
        message: ValidationMessagesTexts.TOASTER.ERROR.SOMETHING_WENT_WRONG,
      });
    });
  }

  onAnalyzeReport(): void {
    this.form.markAllAsTouched();
    this.reportUploadValidationError = '';
    this.dataUploadValidationError = '';
    this.additionalSections.forEach((section) => {
      section.validationError = '';
    });
     if (this.form.invalid) {
       return;
     }
    if (this.getTotalSelectedFilesCount() > UpdateFromDataComponent.MAX_TOTAL_FILES) {
      this.dataUploadValidationError = this.formatMessage(
        UpdateFromDataComponent.MESSAGES.MAX_FILES_ALLOWED,
        { maxFiles: UpdateFromDataComponent.MAX_TOTAL_FILES }
      );
      return;
    }

    if (!this.chevronClientId) {
      this.snackbar.showCustom({ type: 'error', title: ValidationMessagesTexts.TOASTER.ERROR.TITLE, message: UpdateFromDataComponent.API_MESSAGES.CLIENT_CONFIG_NOT_LOADED });
      return;
    }

    const { reportName, reportType, projectId, duration, startMonthYear } = this.form.value;

    const missingFileIndex = this.additionalSections.findIndex(
      (section) => !!section.label.trim() && !section.file,
    );
    if (missingFileIndex !== -1) {
      this.additionalSections[missingFileIndex].validationError = UpdateFromDataComponent.MESSAGES.SECTION_FILE_REQUIRED;
      return;
    }

    const missingLabelIndex = this.additionalSections.findIndex(
      (section) => !!section.file && !section.label.trim(),
    );
    if (missingLabelIndex !== -1) {
      this.additionalSections[missingLabelIndex].validationError = UpdateFromDataComponent.MESSAGES.SECTION_LABEL_REQUIRED;
      return;
    }

    // Build ordered list of all files with their document types
    const fileEntries: UploadFileEntry[] = [];
    if (this.reportFile) {
      fileEntries.push({
        file: this.reportFile,
        documentType: 'PriorReport',
        title: null,
        sequence: null,
      });
    }
    for (const file of this.dataFiles) {
      fileEntries.push({ file, documentType: 'DataFile', title: null, sequence: null });
    }
    for (const section of this.additionalSections) {
      if (section.file) {
        fileEntries.push({
          file: section.file,
          documentType: 'References',
          title: this.buildAdditionalTitle(section.label),
          sequence: section.sequence,
        });
      }
    }

    const totalFiles = fileEntries.length;
    const uploadId = crypto.randomUUID();
    const createReportPayload = {
      reportName,
      clientId: this.chevronClientId,
      reportTypeId: Number(reportType),
      projectId: Number(projectId),
      uploadId,
      userInput: {
        Duration: duration,
        Start: startMonthYear,
      },
      generateReport: true,
    };

    // Split into sequential batches of 3 (each batch uploads in parallel)
    const batches: (typeof fileEntries)[] = [];
    for (let i = 0; i < fileEntries.length; i += 3) {
      batches.push(fileEntries.slice(i, i + 3));
    }

    this.uploadedCount = 0;
    this.uploadProgress = this.formatMessage(UpdateFromDataComponent.API_MESSAGES.UPLOAD_PROGRESS, { uploaded: 0, total: totalFiles });
    this.isSubmitting = true;
    this.isLoading = true;

    from(batches).pipe(
      concatMap(batch =>
        forkJoin(batch.map(entry => {
          return this.provisioningService.uploadFiles(
            {
              projectId: Number(projectId),
              client: this.chevronClientId!.toString(),
              metadata: [
                {
                  fileName: entry.file.name,
                  documentType: entry.documentType,
                  title: entry.title,
                  sequence: entry.sequence,
                },
              ],
              uploadId,
            },
            [entry.file]
          ).pipe(
            tap(() => {
              this.uploadedCount++;
              this.uploadProgress = this.formatMessage(
                UpdateFromDataComponent.API_MESSAGES.UPLOAD_PROGRESS,
                { uploaded: this.uploadedCount, total: totalFiles }
              );
            })
          );
        }))
      )
    ).subscribe({
      error: () => {
        this.isSubmitting = false;
        this.isLoading = false;
        this.uploadProgress = '';
        this.snackbar.showCustom({ type: 'error', title: ValidationMessagesTexts.TOASTER.ERROR.TITLE, message: UpdateFromDataComponent.API_MESSAGES.UPLOAD_FAILED });
      },
      complete: () => {
        this.uploadProgress = UpdateFromDataComponent.API_MESSAGES.STARTING_ANALYSIS;
        this.reportV2Service.createReport(createReportPayload).subscribe({
          next: (response) => {
            this.isSubmitting = false;
            this.isLoading = false;
            this.uploadProgress = '';
            if (response.success) {
              this.reportId = response?.data?.reportId ?? 0;
              this.reportStatusDialogButtonText = '';
              this.reportStatusDialogMessage = `${response.message} Report ID: ${this.reportId}`;
              this.isReportStatusDialogOpen = true;
            } else {
              this.snackbar.showCustom({
                type: 'error',
                title: ValidationMessagesTexts.TOASTER.ERROR.TITLE,
                message: response.message || UpdateFromDataComponent.API_MESSAGES.FAILED_TO_START_ANALYSIS,
              });
            }
          },
          error: () => {
            this.isSubmitting = false;
            this.isLoading = false;
            this.uploadProgress = '';
            this.snackbar.showCustom({ type: 'error', title: ValidationMessagesTexts.TOASTER.ERROR.TITLE, message: UpdateFromDataComponent.API_MESSAGES.SUBMISSION_FAILED });
          },
        });
      },
    });
  }


  //File validation methods
  private async validateFileHealth(file: File, extension: string): Promise<string | null> {
    if (file.size === 0) {
      return this.formatMessage(UpdateFromDataComponent.MESSAGES.FILE_EMPTY_BYTES, { fileName: file.name });
    }

    if (file.size > UpdateFromDataComponent.MAX_FILE_BYTES) {
      return this.formatMessage(UpdateFromDataComponent.MESSAGES.FILE_EXCEEDS_MAX_SIZE, { fileName: file.name });
    }

    let bytes: Uint8Array;
    try {
      bytes = new Uint8Array(await file.arrayBuffer());
    } catch {
      return this.formatMessage(UpdateFromDataComponent.MESSAGES.FILE_UNREADABLE, { fileName: file.name });
    }

    if (!bytes.length) {
      return this.formatMessage(UpdateFromDataComponent.MESSAGES.FILE_EMPTY_BYTES, { fileName: file.name });
    }

    if (extension === 'pdf') {
      return this.validatePdfHealth(file.name, bytes);
    }

    if (extension === 'docx' || extension === 'dotx') {
      return this.validateOpenXmlHealth(file.name, bytes, 'word/');
    }

    if (extension === 'xlsx') {
      return this.validateOpenXmlHealth(file.name, bytes, 'xl/');
    }

    if (extension === 'xls') {
      return this.validateLegacyXlsHealth(file.name, bytes);
    }

    if (extension === 'csv') {
      return this.validateCsvHealth(file.name, bytes);
    }

    return null;
  }

  private validatePdfHealth(fileName: string, bytes: Uint8Array): string | null {
    if (!this.hasAsciiPrefix(bytes, '%PDF-')) {
      return this.formatMessage(UpdateFromDataComponent.MESSAGES.PDF_INVALID, { fileName });
    }

    if (!this.containsAsciiToken(bytes, '%%EOF')) {
      return this.formatMessage(UpdateFromDataComponent.MESSAGES.PDF_INCOMPLETE, { fileName });
    }

    if (this.containsAsciiToken(bytes, '/Encrypt')) {
      return this.formatMessage(UpdateFromDataComponent.MESSAGES.PDF_PASSWORD_PROTECTED, { fileName });
    }

    return null;
  }

  private validateOpenXmlHealth(fileName: string, bytes: Uint8Array, requiredFolderToken: string): string | null {
    if (this.hasOleSignature(bytes)) {
      return this.formatMessage(UpdateFromDataComponent.MESSAGES.OPENXML_INVALID_OR_PROTECTED, { fileName });
    }

    if (!this.hasZipSignature(bytes) || !this.hasZipEndOfCentralDirectory(bytes)) {
      return this.formatMessage(UpdateFromDataComponent.MESSAGES.FILE_CORRUPTED, { fileName });
    }

    if (!this.containsAsciiToken(bytes, '[Content_Types].xml') || !this.containsAsciiToken(bytes, requiredFolderToken)) {
      return this.formatMessage(UpdateFromDataComponent.MESSAGES.OPENXML_INVALID_STRUCTURE, { fileName });
    }

    return null;
  }

  private validateLegacyXlsHealth(fileName: string, bytes: Uint8Array): string | null {
    if (!this.hasOleSignature(bytes)) {
      return this.formatMessage(UpdateFromDataComponent.MESSAGES.XLS_INVALID, { fileName });
    }

    if (this.containsAsciiToken(bytes, 'EncryptedPackage') || this.containsAsciiToken(bytes, 'EncryptionInfo')) {
      return this.formatMessage(UpdateFromDataComponent.MESSAGES.FILE_PASSWORD_PROTECTED, { fileName });
    }

    return null;
  }

  private validateCsvHealth(fileName: string, bytes: Uint8Array): string | null {
    const sample = bytes.subarray(0, Math.min(bytes.length, 4096));
    if (sample.includes(0)) {
      return this.formatMessage(UpdateFromDataComponent.MESSAGES.CSV_INVALID, { fileName });
    }

    const decoder = new TextDecoder('utf-8', { fatal: false });
    const text = decoder.decode(bytes.subarray(0, Math.min(bytes.length, 16384)));
    if (!text.trim()) {
      return this.formatMessage(UpdateFromDataComponent.MESSAGES.FILE_EMPTY, { fileName });
    }

    return null;
  }

  private formatMessage(template: string, values: Record<string, string | number>): string {
    return Object.entries(values).reduce(
      (message, [key, value]) => message.replace(`{${key}}`, String(value)),
      template,
    );
  }

  private hasAsciiPrefix(bytes: Uint8Array, text: string): boolean {
    if (bytes.length < text.length) {
      return false;
    }

    for (let i = 0; i < text.length; i++) {
      if (bytes[i] !== text.charCodeAt(i)) {
        return false;
      }
    }

    return true;
  }

  private containsAsciiToken(bytes: Uint8Array, token: string): boolean {
    const tokenBytes = new TextEncoder().encode(token);
    if (tokenBytes.length === 0 || bytes.length < tokenBytes.length) {
      return false;
    }

    outer: for (let i = 0; i <= bytes.length - tokenBytes.length; i++) {
      for (let j = 0; j < tokenBytes.length; j++) {
        if (bytes[i + j] !== tokenBytes[j]) {
          continue outer;
        }
      }
      return true;
    }

    return false;
  }

  private hasZipSignature(bytes: Uint8Array): boolean {
    if (bytes.length < 4) {
      return false;
    }

    return bytes[0] === 0x50 && bytes[1] === 0x4b && (
      (bytes[2] === 0x03 && bytes[3] === 0x04) ||
      (bytes[2] === 0x05 && bytes[3] === 0x06) ||
      (bytes[2] === 0x07 && bytes[3] === 0x08)
    );
  }

  private hasZipEndOfCentralDirectory(bytes: Uint8Array): boolean {
    if (bytes.length < 22) {
      return false;
    }

    const minSearchIndex = Math.max(0, bytes.length - 65557);
    for (let i = bytes.length - 22; i >= minSearchIndex; i--) {
      if (
        bytes[i] === 0x50 &&
        bytes[i + 1] === 0x4b &&
        bytes[i + 2] === 0x05 &&
        bytes[i + 3] === 0x06
      ) {
        return true;
      }
    }

    return false;
  }

  private hasOleSignature(bytes: Uint8Array): boolean {
    if (bytes.length < 8) {
      return false;
    }

    return (
      bytes[0] === 0xd0 &&
      bytes[1] === 0xcf &&
      bytes[2] === 0x11 &&
      bytes[3] === 0xe0 &&
      bytes[4] === 0xa1 &&
      bytes[5] === 0xb1 &&
      bytes[6] === 0x1a &&
      bytes[7] === 0xe1
    );
  }

  private getExtension(fileName: string): string {
    const dot = fileName.lastIndexOf('.');
    return dot > 0 ? fileName.slice(dot + 1).toLowerCase() : '';
  }

  private sumFileSizes(files: File[]): number {
    return files.reduce((sum, file) => sum + file.size, 0);
  }

  private getDataFilesBytes(): number {
    return this.sumFileSizes(this.dataFiles);
  }

  private getAdditionalFilesCount(excludeIndex?: number): number {
    return this.additionalSections.reduce((count, section, i) => {
      if (excludeIndex !== undefined && i === excludeIndex) {
        return count;
      }
      return section.file ? count + 1 : count;
    }, 0);
  }

  private getAdditionalFilesBytes(excludeIndex?: number): number {
    return this.additionalSections.reduce((sum, section, i) => {
      if (excludeIndex !== undefined && i === excludeIndex) {
        return sum;
      }
      return sum + (section.file?.size ?? 0);
    }, 0);
  }

  private getTotalSelectedFilesCount(): number {
    return (this.reportFile ? 1 : 0) + this.dataFiles.length + this.getAdditionalFilesCount();
  }

  private findDuplicateNames(files: File[]): string[] {
    const seen = new Set<string>();
    const dupes = new Set<string>();
    for (const file of files) {
      const key = file.name.toLowerCase();
      if (seen.has(key)) {
        dupes.add(file.name);
      }
      seen.add(key);
    }
    return Array.from(dupes);
  }

  private getAllSelectedFileNames(excludeAdditionalIndex?: number): Set<string> {
    const names = new Set<string>();
    if (this.reportFile) {
      names.add(this.reportFile.name.toLowerCase());
    }
    for (const file of this.dataFiles) {
      names.add(file.name.toLowerCase());
    }
    this.additionalSections.forEach((section, index) => {
      if (excludeAdditionalIndex !== undefined && index === excludeAdditionalIndex) {
        return;
      }
      if (section.file) {
        names.add(section.file.name.toLowerCase());
      }
    });
    return names;
  }

  private updateAdditionalSectionSequences(): void {
    this.additionalSections.forEach((section, index) => {
      section.sequence = index + 1;
    });
  }

  private buildAdditionalTitle(sectionLabel: string): string {
    const trimmedLabel = sectionLabel.trim();
    if (trimmedLabel.toLowerCase().endsWith('.pdf')) {
      return trimmedLabel.slice(0, -4).trim();
    }

    return trimmedLabel;
  }
}
